/**
 * Native auth-token persistence (P1-12, architecture §J [C14]).
 *
 * On the native bridge the session token lives ONLY in the token vault
 * (native-token-vault.ts: Android Keystore). The Zustand 'auth-storage' blob
 * keeps user, permissions and isAuthenticated, but not the token (see
 * partialize in auth.store.ts), so the token is never written to localStorage
 * or to @capacitor/preferences again, except to the Preferences fallback below
 * while the Keystore refuses writes.
 *
 * Start-up (main.tsx, before the first render):
 *   1. hydrateNativeAuthToken() reads the vault. On the first launch of this
 *      build the vault is empty, so the token found in the legacy plaintext
 *      copies (the localStorage blob, its Preferences mirror, the raw 'token'
 *      keys) is written to the vault, read back, and only then deleted from
 *      every legacy copy. If the vault write fails the legacy copies are kept,
 *      the token is written to the Preferences fallback (the blob loses it on
 *      the next store write, so the next launch would otherwise find nothing)
 *      and it is used for this launch: a responder is never signed out by a
 *      storage migration.
 *   2. startNativeAuthTokenPersistence() mirrors every later token change
 *      (sign-in, device registration, sign-out) into the vault, and into the
 *      Preferences fallback whenever a Keystore write fails. A fallback entry
 *      is moved into the vault on the next launch whose vault write succeeds.
 *
 * Failure handling:
 *   - A vault READ that fails (Keystore busy after boot: KEYSTORE_BUSY) is
 *     retried once. If it still fails and no other copy exists, the session is
 *     NOT ended: the persisted sign-in is kept, hydration is 'deferred'
 *     (native-auth-hydration-state.ts) and retried after a few seconds and on
 *     resume. Only an absent or expired token signs out.
 *   - main.tsx waits for hydration at most NATIVE_HYDRATION_TIMEOUT_MS: a
 *     plugin call that never answers must not leave the life-safety app on a
 *     blank screen. A late result still applies (unless a sign-in or sign-out
 *     happened meanwhile), and the API client does not sign out on a 401 until
 *     hydration has settled.
 *
 * The web console is unchanged: its token stays in the localStorage blob.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { isTokenExpired, reconcileActiveTenantWithToken, useAuthStore } from '@/store/auth.store';
import {
  createPreferencesVault,
  DEVICE_REFRESH_VAULT_KEYS,
  getNativeTokenVault,
  VAULT_KEYS,
  type TokenVault,
} from './native-token-vault';
import { getNativeAuthHydrationState, setNativeAuthHydrationState } from './native-auth-hydration-state';

/** The Zustand persist key of the auth store. */
export const AUTH_BLOB_KEY = 'auth-storage';
/** A raw legacy key some early builds wrote the JWT to (useSocketFactory still reads it). */
export const LEGACY_RAW_TOKEN_KEY = 'token';

/** A key/value store that may hold a legacy plaintext copy of the token. */
export interface LegacyTokenStore {
  readonly name: string;
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export function localStorageLegacyStore(storage: Storage | undefined = safeLocalStorage()): LegacyTokenStore {
  return {
    name: 'localStorage',
    async get(key) {
      return storage ? storage.getItem(key) : null;
    },
    async set(key, value) {
      storage?.setItem(key, value);
    },
    async remove(key) {
      storage?.removeItem(key);
    },
  };
}

export function preferencesLegacyStore(): LegacyTokenStore {
  return {
    name: 'preferences',
    async get(key) {
      return (await Preferences.get({ key })).value ?? null;
    },
    async set(key, value) {
      await Preferences.set({ key, value });
    },
    async remove(key) {
      await Preferences.remove({ key });
    },
  };
}

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** The token inside a persisted Zustand blob, or null. */
export function tokenFromAuthBlob(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { state?: { token?: unknown } } | null;
    return nonEmptyString(parsed?.state?.token);
  } catch {
    return null;
  }
}

/** The same blob with its token removed, or null when there is nothing to change. */
export function authBlobWithoutToken(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { state?: Record<string, unknown> } | null;
    if (!parsed || typeof parsed !== 'object' || !parsed.state || !('token' in parsed.state)) return null;
    const { token: _dropped, ...rest } = parsed.state;
    return JSON.stringify({ ...parsed, state: rest });
  } catch {
    return null;
  }
}

export interface LegacyTokenMigrationResult {
  /** The token to use for this launch (vault copy, else the migrated legacy / fallback one). */
  token: string | null;
  /** A legacy or fallback token was written to the vault on this run. */
  migrated: boolean;
  /** Every legacy plaintext copy (and the fallback entry) was deleted. */
  legacyCleared: boolean;
  /** The vault could not be written; the legacy copies were left in place. */
  vaultWriteFailed: boolean;
  /** The vault could not be read (transient, even after a retry); nothing was written or deleted. */
  vaultReadFailed: boolean;
  /** After a failed vault write, the token was written to the fallback store. */
  fallbackUsed: boolean;
}

/** Delay before the one retry of a failed vault read. */
export const VAULT_READ_RETRY_DELAY_MS = 300;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Read the session token, retrying once: a busy Keystore usually answers the second time. */
async function readVaultToken(vault: TokenVault, retryDelayMs: number): Promise<string | null> {
  try {
    return await vault.get(VAULT_KEYS.sessionToken);
  } catch {
    await sleep(retryDelayMs);
    return vault.get(VAULT_KEYS.sessionToken);
  }
}

export interface MigrationOptions {
  /**
   * Where the token goes when the vault cannot be written (the Preferences
   * vault beside a Keystore vault). It is written only after a failed vault
   * write, so its entry is always newer than the vault's.
   */
  fallback?: TokenVault | null;
  retryDelayMs?: number;
}

/**
 * Move a legacy plaintext (or fallback) token into the vault, then delete the
 * plaintext copies. Deletion happens only after the vault returns the same
 * token on a read-back, so a failed or partial write never loses the session.
 */
export async function migrateLegacyAuthToken(
  vault: TokenVault,
  legacyStores: LegacyTokenStore[],
  options: MigrationOptions = {}
): Promise<LegacyTokenMigrationResult> {
  const fallback = options.fallback ?? null;
  let vaultToken: string | null = null;
  let vaultReadFailed = false;
  try {
    vaultToken = await readVaultToken(vault, options.retryDelayMs ?? VAULT_READ_RETRY_DELAY_MS);
  } catch {
    vaultReadFailed = true;
  }

  let fallbackToken: string | null = null;
  if (fallback) {
    try {
      fallbackToken = nonEmptyString(await fallback.get(VAULT_KEYS.sessionToken));
    } catch {
      fallbackToken = null;
    }
  }

  // Legacy candidates, most authoritative first: the blob the store used,
  // then its mirror, then the raw keys. A non-expired token wins over an
  // expired one.
  const candidates: string[] = [];
  for (const store of legacyStores) {
    try {
      const fromBlob = tokenFromAuthBlob(await store.get(AUTH_BLOB_KEY));
      if (fromBlob) candidates.push(fromBlob);
    } catch {
      // unreadable store: nothing to migrate from it
    }
  }
  for (const store of legacyStores) {
    try {
      const raw = nonEmptyString(await store.get(LEGACY_RAW_TOKEN_KEY));
      if (raw) candidates.push(raw);
    } catch {
      // ignore
    }
  }
  const legacyToken = candidates.find((t) => !isTokenExpired(t)) ?? candidates[0] ?? null;

  if (vaultReadFailed) {
    // The vault's content is unknown, not absent: write nothing, delete nothing.
    return {
      token: fallbackToken ?? legacyToken,
      migrated: false,
      legacyCleared: false,
      vaultWriteFailed: false,
      vaultReadFailed: true,
      fallbackUsed: false,
    };
  }

  // A fallback entry is newer than the vault's (written after a failed vault write).
  const toMigrate = fallbackToken ?? (vaultToken ? null : legacyToken);
  let migrated = false;
  if (toMigrate && toMigrate !== vaultToken) {
    try {
      await vault.set(VAULT_KEYS.sessionToken, toMigrate);
      const readBack = await vault.get(VAULT_KEYS.sessionToken);
      if (readBack !== toMigrate) throw new Error('vault read-back mismatch');
      migrated = true;
    } catch {
      // Keep every plaintext copy, and put the token where the next launch
      // finds it: losing the session would cost more than one more launch
      // with the old storage.
      let fallbackUsed = false;
      if (fallback) {
        try {
          if (fallbackToken !== toMigrate) await fallback.set(VAULT_KEYS.sessionToken, toMigrate);
          fallbackUsed = true;
        } catch {
          fallbackUsed = false;
        }
      }
      return {
        token: toMigrate,
        migrated: false,
        legacyCleared: false,
        vaultWriteFailed: true,
        vaultReadFailed: false,
        fallbackUsed,
      };
    }
  }

  // The vault holds the token (or there is none at all): no plaintext copy
  // may remain, including a stale one left beside an existing vault entry.
  let legacyCleared = true;
  for (const store of legacyStores) {
    try {
      const stripped = authBlobWithoutToken(await store.get(AUTH_BLOB_KEY));
      if (stripped !== null) await store.set(AUTH_BLOB_KEY, stripped);
      await store.remove(LEGACY_RAW_TOKEN_KEY);
    } catch {
      legacyCleared = false;
    }
  }
  if (fallback && fallbackToken) {
    try {
      await fallback.remove(VAULT_KEYS.sessionToken);
    } catch {
      legacyCleared = false;
    }
  }

  return {
    token: toMigrate ?? vaultToken,
    migrated,
    legacyCleared,
    vaultWriteFailed: false,
    vaultReadFailed: false,
    fallbackUsed: false,
  };
}

/**
 * Put the vault token into the auth store, or end a session that has none.
 * Exported for tests; hydrateNativeAuthToken() is the entry point.
 */
export function applyHydratedToken(token: string | null, opts: { keepExpiredDeviceSession?: boolean } = {}): void {
  const state = useAuthStore.getState();
  // An expired device-bound token whose refresh credential is in the vault is
  // kept (W11-BRIDGEKEY): the device refresh renews it, as the MAUI app does on
  // a cold start. Signing out here would also wipe that credential.
  if (token && (!isTokenExpired(token) || (opts.keepExpiredDeviceSession && hasDeviceSessionId(token)))) {
    if (!state.isAuthenticated) return; // a token without a signed-in user is dropped by the persistence subscriber
    if (state.token !== token) useAuthStore.setState({ token });
    reconcileActiveTenantWithToken(token);
    return;
  }
  if (state.isAuthenticated) {
    state.logout('expired');
  } else if (state.token) {
    useAuthStore.setState({ token: null });
  }
}

/** Whether the token carries a device session id (`sid`): a device-bound token. */
export function hasDeviceSessionId(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const claims = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !!claims && typeof claims.sid === 'string' && claims.sid.length > 0;
  } catch {
    return false;
  }
}

/**
 * Whether the vault holds a device refresh credential. A read that fails
 * counts as "yes": a busy Keystore is never a reason to end a session (the
 * refresh itself then answers 'transient' or 'failed').
 */
async function vaultHoldsDeviceRefreshCredential(vault: TokenVault): Promise<boolean> {
  try {
    return !!(await vault.get(VAULT_KEYS.deviceRefreshToken));
  } catch {
    return true;
  }
}

/** The Preferences fallback beside a Keystore vault (none beside any other kind). */
export function fallbackFor(vault: TokenVault): TokenVault | null {
  return vault.kind === 'keystore' ? createPreferencesVault() : null;
}

export interface HydrationDeps {
  vault?: TokenVault;
  /** Default: fallbackFor(vault). */
  fallback?: TokenVault | null;
  legacyStores?: LegacyTokenStore[];
  retryDelayMs?: number;
  /** Delay of the automatic retry after a deferred (transient) vault read. */
  deferredRetryMs?: number;
  /** Default: device-session-refresh.ts refreshDeviceSession (loaded on first use). */
  refreshDeviceSession?: () => Promise<'refreshed' | 'failed' | 'transient'>;
}

async function defaultRefreshDeviceSession(): Promise<'refreshed' | 'failed' | 'transient'> {
  return (await import('./device-session-refresh')).refreshDeviceSession();
}

/** How long after a deferred hydration it is retried on its own (resume retries too). */
export const DEFERRED_HYDRATION_RETRY_MS = 5_000;

let vaultForSession: TokenVault | null = null;
/** The token the last settled hydration left in the store (what the vault holds). */
let hydratedToken: string | null | undefined;
let lastDeps: HydrationDeps | null = null;
let deferredTimer: ReturnType<typeof setTimeout> | null = null;

/** Native only. Call once, before the first render. Never throws. */
export async function hydrateNativeAuthToken(deps: HydrationDeps = {}): Promise<LegacyTokenMigrationResult | null> {
  if (!deps.vault && !Capacitor.isNativePlatform()) return null;
  lastDeps = deps;
  if (deferredTimer) {
    clearTimeout(deferredTimer);
    deferredTimer = null;
  }
  setNativeAuthHydrationState('pending');
  // A sign-in or sign-out while the vault is read wins over what the vault says.
  const tokenAtStart = useAuthStore.getState().token;
  try {
    const vault = deps.vault ?? (await getNativeTokenVault());
    vaultForSession = vault;
    const fallback = deps.fallback !== undefined ? deps.fallback : fallbackFor(vault);
    const result = await migrateLegacyAuthToken(
      vault,
      deps.legacyStores ?? [localStorageLegacyStore(), preferencesLegacyStore()],
      { fallback, retryDelayMs: deps.retryDelayMs }
    );
    if (useAuthStore.getState().token !== tokenAtStart) {
      // hydratedToken stays unknown: persistence writes the newer token.
      setNativeAuthHydrationState('settled');
      return result;
    }
    if (result.vaultReadFailed && !result.token) {
      // Unknown, not absent: keep the persisted sign-in and read again later.
      setNativeAuthHydrationState('deferred');
      deferredTimer = setTimeout(() => {
        deferredTimer = null;
        void retryNativeAuthHydration();
      }, deps.deferredRetryMs ?? DEFERRED_HYDRATION_RETRY_MS);
      return result;
    }
    const keepExpiredDeviceSession =
      !!result.token &&
      isTokenExpired(result.token) &&
      hasDeviceSessionId(result.token) &&
      useAuthStore.getState().isAuthenticated &&
      (await vaultHoldsDeviceRefreshCredential(vault));
    applyHydratedToken(result.token, { keepExpiredDeviceSession });
    // What the vault holds; a refreshed token differs, so persistence writes it.
    hydratedToken = useAuthStore.getState().token;
    if (keepExpiredDeviceSession && useAuthStore.getState().token === result.token) {
      renewHydratedDeviceSession(result.token as string, deps.refreshDeviceSession ?? defaultRefreshDeviceSession);
    }
    if (!useAuthStore.getState().isAuthenticated && result.token && !result.vaultReadFailed) {
      await vault.remove(VAULT_KEYS.sessionToken).catch(() => undefined);
      await vault.remove(VAULT_KEYS.parentToken).catch(() => undefined);
      for (const key of DEVICE_REFRESH_VAULT_KEYS) await vault.remove(key).catch(() => undefined);
      if (fallback) await fallback.remove(VAULT_KEYS.sessionToken).catch(() => undefined);
    }
    setNativeAuthHydrationState('settled');
    return result;
  } catch (err) {
    console.warn('[auth] token vault hydration failed', err);
    setNativeAuthHydrationState('settled');
    return null;
  }
}

/**
 * Renew a kept expired device-bound token in the background (start-up must not
 * wait for the network). Only a definitive refusal ('failed') signs out, and
 * only if that same token is still the session; 'transient' keeps it (the API
 * client tries again before the next request).
 */
function renewHydratedDeviceSession(
  token: string,
  refresh: () => Promise<'refreshed' | 'failed' | 'transient'>
): void {
  hydrationRefresh = refresh()
    .catch((): 'transient' => 'transient')
    .then((outcome) => {
      const state = useAuthStore.getState();
      if (outcome === 'failed' && state.isAuthenticated && state.token === token) state.logout('expired');
      return outcome;
    });
}

let hydrationRefresh: Promise<unknown> = Promise.resolve();

/** For tests: resolves when the start-up device refresh (if any) has been handled. */
export function __hydrationDeviceRefreshForTests(): Promise<unknown> {
  return hydrationRefresh;
}

/** Retry a deferred hydration (app resume, timer). A no-op unless the last read was deferred. */
export async function retryNativeAuthHydration(): Promise<LegacyTokenMigrationResult | null> {
  if (getNativeAuthHydrationState() !== 'deferred' || !lastDeps) return null;
  return hydrateNativeAuthToken(lastDeps);
}

/** Start-up waits for the vault at most this long (main.tsx). */
export const NATIVE_HYDRATION_TIMEOUT_MS = 3_000;

/**
 * Resolve when `work` ends or after `ms`, whichever comes first. Never
 * rejects: start-up renders either way, and `work` keeps running after a
 * timeout (its result still applies when it arrives).
 */
export function withStartupTimeout(
  work: Promise<unknown>,
  ms: number,
  onTimeout?: () => void
): Promise<'done' | 'timeout'> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      onTimeout?.();
      resolve('timeout');
    }, ms);
    work.then(
      () => {
        clearTimeout(timer);
        resolve('done');
      },
      (err) => {
        clearTimeout(timer);
        console.error('[App] native session preparation failed', err);
        resolve('done');
      }
    );
  });
}

let unsubscribe: (() => void) | null = null;
let writes: Promise<unknown> = Promise.resolve();

/**
 * Native only. Mirrors every token change into the vault, in order. A failed
 * vault write puts the token in the fallback store instead (the blob no longer
 * carries it, so without this the next launch would sign the user out).
 * Idempotent.
 */
export function startNativeAuthTokenPersistence(deps: { vault?: TokenVault; fallback?: TokenVault | null } = {}): void {
  if (unsubscribe) return;
  if (!deps.vault && !Capacitor.isNativePlatform()) return;
  const vaultPromise = deps.vault
    ? Promise.resolve(deps.vault)
    : vaultForSession
      ? Promise.resolve(vaultForSession)
      : getNativeTokenVault();

  const persist = (token: string | null) => {
    writes = writes
      .then(async () => {
        const vault = await vaultPromise;
        const fallback = deps.fallback !== undefined ? deps.fallback : fallbackFor(vault);
        if (token) {
          try {
            await vault.set(VAULT_KEYS.sessionToken, token);
          } catch (err) {
            if (!fallback) throw err;
            console.warn('[auth] token vault write failed; keeping the token in the fallback store', err);
            await fallback.set(VAULT_KEYS.sessionToken, token);
            return;
          }
          if (fallback) await fallback.remove(VAULT_KEYS.sessionToken).catch(() => undefined);
        } else {
          await vault
            .remove(VAULT_KEYS.sessionToken)
            .catch((err) => console.warn('[auth] token vault remove failed', err));
          await vault
            .remove(VAULT_KEYS.parentToken)
            .catch((err) => console.warn('[auth] token vault remove failed', err));
          // The device session's refresh credential, nonce and bound key id (W11-BRIDGEKEY).
          for (const key of DEVICE_REFRESH_VAULT_KEYS) {
            await vault.remove(key).catch((err) => console.warn('[auth] token vault remove failed', err));
          }
          if (fallback) await fallback.remove(VAULT_KEYS.sessionToken).catch(() => undefined);
        }
      })
      .catch((err) => console.warn('[auth] token vault write failed', err));
  };

  unsubscribe = useAuthStore.subscribe((state, prev) => {
    if (state.token !== prev.token) persist(state.token);
  });
  // A sign-in that landed before this started (start-up rendered on the
  // hydration timeout) has not been written yet.
  const current = useAuthStore.getState().token;
  if (current && current !== hydratedToken) persist(current);
}

/** Resolves when every queued vault write has finished. */
export function flushNativeAuthTokenWrites(): Promise<unknown> {
  return writes;
}

/** For tests. */
export function __stopNativeAuthTokenPersistenceForTests(): void {
  unsubscribe?.();
  unsubscribe = null;
  writes = Promise.resolve();
  vaultForSession = null;
  hydratedToken = undefined;
  lastDeps = null;
  hydrationRefresh = Promise.resolve();
  if (deferredTimer) clearTimeout(deferredTimer);
  deferredTimer = null;
  setNativeAuthHydrationState('settled');
}

/** The vault this session hydrated from (device-session.service keeps the parent token in it). */
export async function getSessionTokenVault(): Promise<TokenVault | null> {
  if (vaultForSession) return vaultForSession;
  if (!Capacitor.isNativePlatform()) return null;
  return getNativeTokenVault();
}
